/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * The reader surface consumed by @nettrek/c2pa-hls-bridge — a structural subset
 * of the @contentauth/c2pa-web `Reader`. Built from a parsed manifest store plus
 * the source bytes (for resource resolution and, in later steps, verification).
 */

import type { Manifest, ManifestStore } from '../types'
import { readC2paBox } from '../bmff/c2paBox'
import { parseJumbf } from '../jumbf/parseJumbf'
import { parseManifestStore, type ParsedStore } from '../model/parseManifestStore'
import { toManifestStore, type MapOptions } from '../map/toManifestStore'
import type { ManifestVerification } from '../verify/verifyManifest'

/** The reader surface (mirrors the parts of c2pa-web's `Reader` the bridge uses). */
export interface Reader {
    manifestStore: () => Promise<ManifestStore>
    activeManifest: () => Promise<Manifest | null>
    activeLabel: () => Promise<string | null>
    crJson: () => Promise<unknown>
    resourceToBytes: (uri: string) => Promise<Uint8Array>
    free: () => Promise<void>
}

/** Inputs for constructing a reader from BMFF bytes. */
export interface ReaderInput {
    /** Init / whole-asset bytes carrying the manifest store. */
    init: Uint8Array
    /** Optional media fragment bytes (HLS/DASH per-fragment validation). */
    fragment?: Uint8Array
}

export class WebCryptoReader implements Reader {
    readonly #parsed: ParsedStore
    readonly #input: ReaderInput
    /** Filled by the verification pipeline (steps 4–7); empty here. */
    #mapOptions: MapOptions = {}
    /** Per-manifest verification results, set by the verification pipeline. */
    verifications: ManifestVerification[] = []

    private constructor (parsed: ParsedStore, input: ReaderInput) {
        this.#parsed = parsed
        this.#input = input
    }

    /** Parses the manifest store from the input, or returns null when no C2PA box is present. */
    static create (input: ReaderInput): WebCryptoReader | null {
        const box = readC2paBox(input.init)
        // manifest / original / update all carry a JUMBF manifest store; merkle does not.
        if (!box || box.purpose === 'merkle') return null
        const root = parseJumbf(box.jumbf)
        if (!root || root.type !== 'c2pa') return null
        const parsed = parseManifestStore(root, box.jumbf)
        if (parsed.manifests.length === 0) return null
        return new WebCryptoReader(parsed, input)
    }

    /** Internal: the parsed model, for the verification pipeline. */
    get parsed (): ParsedStore {
        return this.#parsed
    }

    /** Internal: the source bytes, for verification / resource resolution. */
    get input (): ReaderInput {
        return this.#input
    }

    /** Internal: supply verification results to be folded into the store. */
    setMapOptions (opts: MapOptions): void {
        this.#mapOptions = opts
    }

    async manifestStore (): Promise<ManifestStore> {
        return toManifestStore(this.#parsed, this.#mapOptions)
    }

    async activeManifest (): Promise<Manifest | null> {
        const store = await this.manifestStore()
        const active = store.active_manifest
        return active ? store.manifests?.[active] ?? null : null
    }

    async activeLabel (): Promise<string | null> {
        return this.#parsed.activeLabel
    }

    async crJson (): Promise<unknown> {
        // The addon does not consume crJSON; expose the manifest store so callers
        // that expect a JSON snapshot still get the data.
        return this.manifestStore()
    }

    /**
     * Resolves a JUMBF resource URI (e.g.
     * `self#jumbf=/c2pa/<manifest>/c2pa.assertions/<label>`) to its bytes — used
     * for ingredient/claim thumbnails. Returns an empty array when unresolved.
     */
    async resourceToBytes (uri: string): Promise<Uint8Array> {
        // manifest label = segment after /c2pa/ ; assertion label = last segment
        const manifestMatch = uri.match(/\/c2pa\/([^/]+)/)
        const manifestLabel = manifestMatch ? manifestMatch[1] : this.#parsed.activeLabel
        const assertionLabel = uri.slice(uri.lastIndexOf('/') + 1)
        const manifest = manifestLabel ? this.#parsed.byLabel.get(manifestLabel) : undefined
        const assertion = manifest?.assertions.find(a => a.label === assertionLabel)
        return assertion?.raw ?? new Uint8Array(0)
    }

    async free (): Promise<void> {
        // No worker/wasm to release.
    }
}
